import { api } from './index'
import { ensureOk, unwrap } from './client'
import type { components, operations } from './schema'

export type MeUser = components['schemas']['MeUser']
export type AuthToken = components['schemas']['AuthToken']
export type RevokedCount = components['schemas']['RevokedCount']
export type RecoveryCodes = components['schemas']['RecoveryCodes']
/** What the invite page sends; the realm is not the page's to choose, `acceptInvite` adds it. */
export type InviteAcceptance = Omit<
  operations['inviteAccept']['requestBody']['content']['application/json'],
  'realm'
>

/**
 * Exchanges credentials for a pre-MFA token. The realm goes with them so a
 * tenant account is refused at the door (403 realm_mismatch) rather than
 * after it has set up or verified its authenticator.
 */
export async function login(email: string, password: string): Promise<AuthToken> {
  return unwrap<AuthToken>(
    await api.POST('/api/v1/auth/login', { body: { email, password, realm: 'operator' } }),
  )
}

/** Upgrades a verify_required token to a full one. */
export async function verifyMfa(code: string): Promise<AuthToken> {
  return unwrap<AuthToken>(await api.POST('/api/v1/auth/mfa/verify', { body: { code } }))
}

/** Re-validates the stored token and returns who it belongs to. */
export async function fetchMe(): Promise<MeUser> {
  return unwrap<MeUser>(await api.GET('/api/v1/auth/me'))
}

export async function updateMe(changes: {
  name?: string
  timezone?: string | null
}): Promise<MeUser> {
  return unwrap<MeUser>(await api.PATCH('/api/v1/auth/me', { body: changes }))
}

/** Revokes the token server-side. The caller still clears it locally. */
export async function logout(): Promise<void> {
  await api.POST('/api/v1/auth/logout')
}

export interface MfaSetup {
  secret: string
  otpauth_url: string
}

export type MfaConfirmed = AuthToken & { recovery_codes: string[] }

export interface StepUpWindow {
  step_up_valid_until: string
}

/** Starts first-time enrolment. Each call replaces any unconfirmed secret. */
export async function mfaSetup(): Promise<MfaSetup> {
  return unwrap<MfaSetup>(await api.POST('/api/v1/auth/mfa/setup'))
}

/** Proves the authenticator has the secret. The recovery codes come back once. */
export async function mfaConfirm(code: string): Promise<MfaConfirmed> {
  return unwrap<MfaConfirmed>(await api.POST('/api/v1/auth/mfa/confirm', { body: { code } }))
}

/** Consumes one recovery code in place of an authenticator code. */
export async function recover(code: string): Promise<AuthToken> {
  return unwrap<AuthToken>(await api.POST('/api/v1/auth/recovery', { body: { code } }))
}

/** Opens the five-minute window that destructive routes check. */
export async function stepUp(body: { password: string } | { code: string }): Promise<StepUpWindow> {
  return unwrap<StepUpWindow>(await api.POST('/api/v1/auth/step-up', { body }))
}

/**
 * Changes the caller's own password, behind step-up. The API revokes every
 * other session and keeps this one, so the answer is how many sessions went.
 */
export async function changePassword(body: {
  current_password: string
  password: string
  password_confirmation: string
}): Promise<RevokedCount> {
  return unwrap<RevokedCount>(await api.POST('/api/v1/auth/password', { body }))
}

/**
 * Starts replacing the caller's authenticator, behind step-up. The secret is
 * pending: the current app keeps working until `mfaReplaceConfirm` proves the
 * new one, and each call replaces the pending secret.
 */
export async function mfaReplace(): Promise<MfaSetup> {
  return unwrap<MfaSetup>(await api.POST('/api/v1/auth/mfa/replace'))
}

/**
 * Proves the new authenticator with a code from it. The pending secret takes
 * over, the old app stops working and a fresh set of recovery codes comes
 * back once. 409 `mfa_replace_not_started` when nothing is pending; 422 on a
 * wrong code, and the old app stays.
 */
export async function mfaReplaceConfirm(code: string): Promise<RecoveryCodes> {
  return unwrap<RecoveryCodes>(
    await api.POST('/api/v1/auth/mfa/replace/confirm', { body: { code } }),
  )
}

/** Issues new recovery codes behind step-up; every earlier code stops working. */
export async function regenerateRecoveryCodes(): Promise<RecoveryCodes> {
  return unwrap<RecoveryCodes>(await api.POST('/api/v1/auth/mfa/recovery-codes'))
}

/** Always accepted, so the answer never says whether the address is known. */
export async function forgotPassword(email: string): Promise<void> {
  ensureOk(await api.POST('/api/v1/auth/password/forgot', { body: { email } }))
}

/**
 * Single-use invite token in, a setup_required token out: MFA enrolment is the
 * only next step. The realm goes with it so a tenant invitation opened on this
 * origin is refused (403 realm_mismatch) before the token is spent, and the
 * same link still works on the tenant's console (#14).
 */
export async function acceptInvite(body: InviteAcceptance): Promise<AuthToken> {
  return unwrap<AuthToken>(
    await api.POST('/api/v1/auth/invite/accept', { body: { ...body, realm: 'operator' } }),
  )
}

export async function resetPassword(body: {
  email: string
  token: string
  password: string
  password_confirmation: string
}): Promise<void> {
  ensureOk(await api.POST('/api/v1/auth/password/reset', { body }))
}
